import { Button, styled, Tooltip } from "@mui/material";
import numeral from "numeral";
import { useEffect, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import CustomDataGrid from "../../../common/js/components/CustomDataGrid";
import StatusChip from "../../../common/js/components/StatusChip";
import { pageSize } from "../utils/constants";
import { buildAddress } from "../utils/operations";

const StyledLink = styled(RouterLink)({
    textDecoration: "none",
    lineHeight: "1.2",
});

const StyledDealName = styled("span")({
    "&:hover": {
        cursor: "pointer",
        textDecoration: "underline",
    },
    color: "#2C99D2",
    fontWeight: 600,
    wordWrap: "break-word",
    whiteSpace: "normal",
    lineHeight: "1.3",
});

const StyledDealCell = styled("div")({
    display: "flex",
    flexDirection: "column",
});

const StyledAddress = styled("div")({
    fontSize: "12px",
});

const StyledButton = styled(Button)({
    height: "28px",
    marginLeft: "0px",
});

const CorporateDataDealsGrid = ({
    corporateDataDeals,
    dealsPage,
    dealView,
    getCorporateDataDeals,
    handleStartReviewingAsCorporateData,
    isLoading,
    resetDealsTab,
    setDealsPage,
    setDealsSort,
}) => {
    const navigate = useNavigate();
    const [sortModel, setSortModel] = useState([]);
    const dataDeals = corporateDataDeals?.content ?? [];
    const totalCount = corporateDataDeals?.totalElements ?? 0;
    const [columnVisibilityModel, setColumnVisibilityModel] = useState({ dealStatus: false });

    useEffect(() => {
        resetDealsTab();
        getCorporateDataDeals();
        handleColumnVisibilityChange();
    }, [dealView]);

    const renderBrokers = (brokers) => {
        if (!brokers) return "";
        const brokerNames = brokers.split(",");
        if (brokerNames.length > 1) {
            return (
                <Tooltip title={brokers} placement="top">
                    <span>{`${brokerNames.length} Brokers`}</span>
                </Tooltip>
            );
        }
        return brokers;
    };

    const handleAction = async (action, deal) => {
        if (action === "Start") {
            const result = await handleStartReviewingAsCorporateData(deal, `/comp/${deal.id}`);
            if (result && result.dealKey) {
                navigate(`/comp/${result.dealKey}`);
            }
        } else {
            navigate(`/deal/${deal.id}`);
        }
    };

    const columns = [
        {
            field: "dealName",
            headerName: "Deal",
            flex: 2,
            minWidth: 250,
            renderCell: (params) => (
                <StyledDealCell>
                    <StyledLink to={`/deal/${params.id}`}>
                        <StyledDealName>
                            {`Deal No. ${params.id}${params.row?.dealName ? ` | ${params.row.dealName}` : ""}`}
                        </StyledDealName>
                    </StyledLink>
                </StyledDealCell>
            ),
        },
        {
            field: "address",
            headerName: "Address",
            flex: 2,
            minWidth: 280,
            sortable: false,
            renderCell: (params) => (
                <StyledDealCell>
                    <StyledAddress>{buildAddress(params.row?.primaryAddress ?? {})}</StyledAddress>
                </StyledDealCell>
            ),
        },
        {
            field: "brokers",
            headerName: "Brokers",
            flex: 1,
            minWidth: 120,
            renderCell: (params) => renderBrokers(params.row?.brokers),
        },
        {
            field: "propertyType",
            headerName: "Type",
            flex: 1,
            minWidth: 120,
            sortable: false,
            renderCell: (params) => `${params.row?.propertyType} ${params.row?.transactionType}`,
        },
        {
            field: "totalCommission",
            headerName: "Total Commission",
            hide: true,
            flex: 1,
            minWidth: 150,
            renderCell: (params) =>
                params.row?.totalCommission ? "$ " + numeral(params.row.totalCommission).format("0,0.00") : "",
        },
        {
            field: "dealStatus",
            headerName: "Status",
            flex: 1,
            minWidth: 170,
            renderCell: (params) => {
                return <StatusChip status={params.row?.dealStatus ?? ""} />;
            },
        },
        {
            field: "action",
            headerName: "Action",
            flex: 1,
            minWidth: 100,
            renderCell: (params) => {
                return (
                    <StyledButton
                        variant={params.row?.action === "Start" ? "contained" : "outlined"}
                        color="primary"
                        onClick={() => handleAction(params.row?.action, params.row)}
                    >
                        {params.row.action}
                    </StyledButton>
                );
            },
        },
    ];

    const handlePageChange = (value) => {
        setDealsPage(value);
        getCorporateDataDeals();
    };

    const handleSortModelChange = (newSortModel) => {
        setDealsPage(1);
        setSortModel(newSortModel);
        const newSort =
            newSortModel[0]?.field && newSortModel[0]?.sort ? `${newSortModel[0].field},${newSortModel[0].sort}` : null;
        setDealsSort(newSort);
        getCorporateDataDeals();
    };

    const handleColumnVisibilityChange = () => {
        columnVisibilityModel["dealStatus"] = dealView === "data_entry_open";
        setColumnVisibilityModel(columnVisibilityModel);
    };

    return (
        <CustomDataGrid
            rows={dataDeals}
            columns={columns}
            totalCount={totalCount}
            page={dealsPage}
            rowHeight={35}
            pageSize={pageSize}
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            onPageChange={(_, value) => handlePageChange(value)}
            loading={isLoading}
            columnVisibilityModel={columnVisibilityModel}
        />
    );
};

export default CorporateDataDealsGrid;
